import { fetchFundingStages } from '@/api/v4/funding-stages.api';
import { fetchHQLocations } from '@/api/v4/locations.api';
import { fetchEmployeeRanges } from '@/api/v4/organization.api';
import { fetchListingStages } from '@/api/v4/project-listings.api';
import { fetchTags } from '@/api/v4/tags.api';
import { QueryKey } from '@/config/query-client';
import { useQueries } from '@tanstack/react-query';
import { companiesFilterOptionsState } from '../companies.state';
import {
  getCapitalRaisedOptions,
  getRankingOptions,
  mapCompanyFundingStageFilter,
  mapCompanyStatusFilter,
} from './company-filters-helpers';
import { getYearsOptions } from '@/utils/years';
import { mapEmployeesRangesToOptions } from '@/hooks/queries/use-employees-ranges.query';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetAtom } from 'jotai';

export const useFetchCompanyFiltersOptions = () => {
  const [isFetchingCompanyFilters, setIsFetchingCompanyFilters] =
    useState(true);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search.split('?')[1]);
  const locationsParams = searchParams.getAll('hqLocationCity');
  const setDefaultFilterOptions = useSetAtom(companiesFilterOptionsState);
  const staleTime = 5 * 60 * 1000;
  const cacheTime = 5 * 60 * 1000;

  const queries = useQueries({
    queries: [
      {
        queryKey: [QueryKey.Tags],
        queryFn: fetchTags,
        staleTime,
        gcTime: cacheTime,
      },
      {
        queryKey: [QueryKey.EmployeeRanges],
        queryFn: fetchEmployeeRanges,
        staleTime,
        gcTime: cacheTime,
      },
      {
        queryKey: [QueryKey.FundingStages],
        queryFn: fetchFundingStages,
        staleTime,
        gcTime: cacheTime,
      },
      {
        queryKey: [QueryKey.ListingStages],
        queryFn: () => fetchListingStages(),
        staleTime,
        gcTime: cacheTime,
      },
      {
        queryKey: [QueryKey.HqLocations, { cityId: locationsParams }],
        queryFn: () =>
          fetchHQLocations({
            ...(locationsParams.length > 0 && { cityId: locationsParams }),
          }),
        staleTime,
        gcTime: cacheTime,
      },
    ],
  });

  const tagsData = queries[0].data;
  const employeeRangesData = queries[1].data;
  const fundingStagesData = queries[2].data;
  const listingStagesData = queries[3].data;
  const hqLocationsData = queries[4].data;

  useEffect(() => {
    setDefaultFilterOptions(currentFilters => ({
      ...currentFilters,
      ...(tagsData && {
        tags: tagsData.map(tag => ({ value: tag.id, label: tag.name })),
      }),
      ...(employeeRangesData && {
        employeesRange: mapEmployeesRangesToOptions(employeeRangesData),
      }),
      ...(fundingStagesData && {
        fundingStages: fundingStagesData.map(mapCompanyFundingStageFilter),
      }),
      ...(listingStagesData && {
        listingStatuses: listingStagesData.map(mapCompanyStatusFilter),
      }),
      ...(hqLocationsData && {
        hqCities: hqLocationsData.map(location => ({
          value: location.id,
          label: location.description,
        })),
      }),
    }));
  }, [
    tagsData,
    employeeRangesData,
    fundingStagesData,
    listingStagesData,
    hqLocationsData,
    setDefaultFilterOptions,
  ]);

  const allQueriesFinished = queries.every(
    result => !result.isLoading && result.isSuccess,
  );

  useEffect(() => {
    if (allQueriesFinished) {
      setDefaultFilterOptions(currentFilters => ({
        ...currentFilters,
        yearFounded: getYearsOptions(),
        ranking: getRankingOptions(),
        capitalRaised: getCapitalRaisedOptions(),
      }));
      setIsFetchingCompanyFilters(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allQueriesFinished]);

  return {
    isFetchingCompanyFilters,
  };
};
